// Update lifecycle — ownership manifest + `vortex update --templates-only`.
//
// VortEX ships framework-owned templates (the per-agent routers, the slash-
// command prompts, the instance config) that `vortex init` copies into an
// instance once. After that they are "install-once": a newer `@vortex-os/base`
// improves the shipped templates, but the instance's copies never refresh, so
// the improvement is stranded. This module closes that gap WITHOUT ever losing
// a user's edits.
//
// The transaction model is the whole point (see docs/STATUS.md "Update
// lifecycle"):
//   - never overwrite a framework file the user has edited — surface a
//     `<file>.new` alongside it and leave the user's file untouched;
//   - never record a manifest state that disagrees with disk — back up before
//     replacing, write each file atomically (temp → rename), apply each file
//     under its own try/catch and record the ACTUAL post-state, and write the
//     manifest LAST, so even a crash/partial failure mid-update leaves the
//     manifest agreeing with disk (never a stale claim that turns an already-
//     applied file into a phantom conflict on the next run).
//
// The "ownership manifest" (`data/.vortex/ownership.json`) is how we tell a
// pristine instance copy (safe to replace) from a user-edited one (must not be
// clobbered): for every framework-owned file it records `sourceSha256` (the
// shipped template's hash when last reconciled) and `installedSha256` (the hash
// of the copy on disk when it matches the template). On update we compare the
// file on disk to `installedSha256` — equal ⇒ pristine ⇒ replaceable; diverged
// ⇒ user-edited ⇒ conflict. `installedSha256: null` marks a slot whose on-disk
// file diverges from the template (a foreign / user-owned file) — never auto-
// replaced. Two escape hatches keep a `null` slot from being stranded forever:
// if its on-disk file later becomes byte-identical to the template, a plain
// update silently re-tracks it (nothing to lose); and `update --adopt <path>`
// force-refreshes a named diverged file to the template (backing up the prior
// bytes first) and re-tracks it — config files are user-owned and refused.
//
// `--templates-only` is local and needs no network: the "newer templates" come
// from the currently-installed package's `templates/` dir (the same dir `init`
// copies from). Package upgrade + re-exec + version awareness are later build-
// order items; this module is MVP step (1).

import { createHash } from "node:crypto";
import { existsSync } from "node:fs";
import { copyFile, mkdir, readFile } from "node:fs/promises";
import { dirname, isAbsolute, join, relative, sep } from "node:path";
import { atomicWriteFile, type ModuleContext } from "@vortex-os/core";

/**
 * Schema tag for the ownership manifest; bump on a breaking shape change.
 * v2: hashes are EOL-normalized (LF) content hashes, not raw bytes — a pre-v2
 * (raw-byte) manifest is migrated in memory on read (see `migrateOwnershipToV2`).
 */
export const OWNERSHIP_SCHEMA = "vortex-ownership/2";

/** The pre-v2 schema (raw-byte hashes) — the ONLY schema we migrate from. */
const OWNERSHIP_SCHEMA_V1 = "vortex-ownership/1";

/** A single framework-owned file the instance tracks for updates. */
export interface OwnershipEntry {
  /** Stable id across path moves — currently the template-relative path. */
  readonly templateId: string;
  /** Instance destination, repoRoot-relative, forward-slashed (portable). */
  readonly path: string;
  /** sha256 of the shipped template when this entry was last reconciled. */
  readonly sourceSha256: string;
  /**
   * When the on-disk file equals the current template, this holds that hash
   * (the copy is pristine — safe to refresh, since identical content has
   * nothing to lose, and the first time the user edits it the hash diverges
   * and it becomes a conflict instead). `null` means the on-disk file diverges
   * from the template — a foreign / user-owned file we never auto-replace.
   */
  readonly installedSha256: string | null;
}

export interface OwnershipManifest {
  readonly schema: string;
  /** The `@vortex-os/base` version whose template index this reconciles to. */
  readonly baseVersion: string;
  /** ISO timestamp the manifest was written. */
  readonly generatedAt: string;
  readonly files: readonly OwnershipEntry[];
}

/** The build-time template index shipped at `templates/manifest.json`. */
interface TemplateIndex {
  readonly schema: string;
  readonly baseVersion: string;
  readonly files: readonly { templateId: string; path: string; sha256: string }[];
}

export type UpdateFileActionKind =
  | "replace" // pristine instance copy → refreshed to the new template
  | "restore" // framework file was missing → re-written from the template
  | "install" // new framework file in this version → written for the first time
  | "conflict" // user edited the file → new template written to `<file>.new`
  | "locally-modified" // user edited it but the template is unchanged → left alone
  | "unmanaged" // foreign slot (installedSha256 === null) → skipped
  | "adopt" // user named it in --adopt → overwritten with the template and re-tracked
  | "removed-upstream" // template no longer shipped → file kept, dropped from manifest
  | "unchanged"; // already matches the current template

export interface UpdateFileAction {
  readonly path: string;
  readonly templateId: string;
  readonly action: UpdateFileActionKind;
  readonly detail?: string;
  /** For `replace` (or a backed-up `.new`): where the prior bytes were saved. */
  readonly backupPath?: string;
  /** For `conflict`: the `<file>.new` carrying the new template content. */
  readonly newFilePath?: string;
  /** Set when applying this file threw — the file was NOT changed destructively. */
  readonly error?: string;
}

export interface VortexUpdateResult {
  readonly subcommand: "update";
  readonly status:
    | "ok" // nothing to do — everything already current
    | "updated" // files were replaced/restored/installed
    | "conflicts" // one or more user-edited files left a `<file>.new`
    | "dry-run" // preview only, nothing written
    | "no-manifest" // instance has no ownership manifest (run init / adopt)
    | "no-templates"; // no shipped template index found (broken install)
  readonly mode: "templates-only";
  readonly dryRun: boolean;
  /** Instance's recorded base version before the update (manifest baseVersion). */
  readonly fromVersion?: string;
  /** Shipped template index's base version (what we reconcile to). */
  readonly toVersion?: string;
  readonly actions: readonly UpdateFileAction[];
  readonly summary: {
    readonly replaced: number;
    readonly restored: number;
    readonly installed: number;
    readonly conflicts: number;
    readonly unchanged: number;
    readonly unmanaged: number;
    /** Files force-refreshed to the template via `--adopt` (prior bytes backed up). */
    readonly adopted: number;
    readonly locallyModified: number;
    readonly removedUpstream: number;
    /** Files whose apply threw (left unchanged) — surfaces a partial run. */
    readonly errors: number;
  };
  readonly nextActions: readonly string[];
}

const MANIFEST_NAME = "manifest.json";

/** Absolute path of the instance ownership manifest. */
export function ownershipManifestPath(ctx: ModuleContext): string {
  return join(ctx.dataDir, ".vortex", "ownership.json");
}

/**
 * The repoRoot-relative, POSIX, trailing-slash prefix of the framework's
 * bookkeeping tree (`data/.vortex/` — the ownership manifest and update
 * backups). This is framework-GENERATED plumbing the user never hand-edits, so:
 *  - the session-start carryover warning excludes it (not the user's WIP), and
 *  - `vortex update` auto-commits the manifest under it (see
 *    `committableUpdatePaths`).
 * Derived from `ctx.dataDir` (not hardcoded `data/`) so a non-default data dir
 * still resolves correctly.
 */
export function frameworkBookkeepingPrefix(ctx: ModuleContext): string {
  return toPosix(relative(ctx.repoRoot, join(ctx.dataDir, ".vortex"))) + "/";
}

/** True when a repoRoot-relative POSIX path lies in the bookkeeping tree above. */
export function isFrameworkBookkeepingPath(ctx: ModuleContext, repoRelPosix: string): boolean {
  const prefix = frameworkBookkeepingPrefix(ctx);
  return repoRelPosix === prefix.slice(0, -1) || repoRelPosix.startsWith(prefix);
}

/**
 * The repoRoot-relative, POSIX paths an update may safely commit so it leaves no
 * uncommitted trail: the framework files it just (re)wrote to the shipped
 * template — `replace` / `restore` / `install` / `adopt` (all now byte-identical
 * to the template, with any prior bytes already backed up) — plus the ownership
 * manifest it rewrote to record the new state. Deliberately EXCLUDES:
 *  - `conflict` — the user's file is untouched and a `<file>.new` awaits a manual
 *    merge; auto-committing the `.new` (or the unchanged user file) would be wrong;
 *  - `locally-modified` / `unmanaged` — the user owns those bytes;
 *  - errored ops — the file was not changed.
 * The caller still checks `git status` per path, so a path with no actual change
 * is a harmless no-op. Pure — never touches git or disk.
 */
export function committableUpdatePaths(ctx: ModuleContext, result: VortexUpdateResult): string[] {
  const out = new Set<string>();
  for (const a of result.actions) {
    if (a.error) continue;
    if (a.action === "replace" || a.action === "restore" || a.action === "install" || a.action === "adopt") {
      out.add(a.path); // already repoRoot-relative POSIX
    }
  }
  out.add(toPosix(relative(ctx.repoRoot, ownershipManifestPath(ctx))));
  return [...out];
}

/** Forward-slash a path so the manifest is portable across OSes. */
function toPosix(p: string): string {
  return p.split(sep).join("/");
}

/** The npm scope whose dependency pins `vortex update` may auto-commit. */
const VORTEX_SCOPE = "@vortex-os/";

/** The package.json sections where a framework install rewrites pins. */
const PIN_SECTIONS = ["dependencies", "devDependencies", "optionalDependencies"] as const;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Structural equality over JSON-parsed values (objects/arrays/primitives). */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => deepEqual(v, b[i]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    return ka.every((k) => Object.prototype.hasOwnProperty.call(b, k) && deepEqual(a[k], b[k]));
  }
  return false;
}

/**
 * `obj` minus the `@vortex-os/*` entries of the three pin sections. A section
 * left EMPTY by that filter is removed entirely, so "no dependencies key" and
 * "dependencies that were all vortex pins" normalize to the same shape — a
 * fresh instance whose first `npm i @vortex-os/base` CREATES the dependencies
 * section must still compare equal to a HEAD that never had one. The kept maps
 * are null-prototype: a JSON key literally named `__proto__` must remain an
 * ordinary, comparable data property (on a `{}` map, `kept["__proto__"] = v`
 * silently sets the prototype instead, which would HIDE a foreign change to
 * that key from the comparison).
 */
function stripVortexPins(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...obj };
  for (const section of PIN_SECTIONS) {
    const deps = out[section];
    if (!isPlainObject(deps)) continue;
    const kept: Record<string, unknown> = Object.create(null);
    for (const [name, range] of Object.entries(deps)) {
      if (!name.startsWith(VORTEX_SCOPE)) kept[name] = range;
    }
    if (Object.keys(kept).length === 0) delete out[section];
    else out[section] = kept;
  }
  return out;
}

/**
 * Is the difference between two package.json texts confined to `@vortex-os/*`
 * dependency pins (changed, added, or removed in dependencies/devDependencies/
 * optionalDependencies)? That is the safety test behind auto-committing the
 * pin change `npm i @vortex-os/...` leaves in the instance repo: everything
 * OUTSIDE that scope must be structurally identical (key order and EOL are
 * irrelevant — both sides are parsed, not text-diffed). Unparseable input is
 * `false` (never auto-commit what we cannot read). NOTE this proves the diff
 * is vortex-SCOPED, not machine-MADE — a hand-edited pin that npm then
 * installed is indistinguishable and gets committed too; that residue risk is
 * accepted (the commit is scoped, transparent, and revertable in git).
 */
export function isVortexScopedPackageDiff(headText: string, worktreeText: string): boolean {
  let head: unknown;
  let worktree: unknown;
  try {
    head = JSON.parse(headText);
    worktree = JSON.parse(worktreeText);
  } catch {
    return false;
  }
  if (!isPlainObject(head) || !isPlainObject(worktree)) return false;
  return deepEqual(stripVortexPins(head), stripVortexPins(worktree));
}

/**
 * Is the difference between two package-lock.json texts confined to
 * `@vortex-os/*` package entries and lockfile metadata? Used for the
 * lockfile-ONLY residue (package.json untouched — e.g. npm refreshing
 * metadata, the 2026-06-12 divergence) AND as the companion check when a
 * vortex-scoped package.json change brings the lockfile along: entries under
 * `node_modules/@vortex-os/` (at any nesting), the root `""` entry's
 * `@vortex-os/*` pins, the legacy v1 `dependencies` map's `@vortex-os/*` keys,
 * and `lockfileVersion` may differ; anything else — a hoisted transitive dep
 * (`node_modules/yaml`), a foreign pin inside the root entry — must be
 * identical, because a change there could be the user's own unrelated work.
 * Unparseable input is `false`. pnpm/yarn lockfiles are NOT parsed by this
 * (YAML/custom formats) — their lockfile-only changes are reported, not
 * committed.
 */
export function isVortexScopedLockfileDiff(headText: string, worktreeText: string): boolean {
  let head: unknown;
  let worktree: unknown;
  try {
    head = JSON.parse(headText);
    worktree = JSON.parse(worktreeText);
  } catch {
    return false;
  }
  if (!isPlainObject(head) || !isPlainObject(worktree)) return false;
  return deepEqual(stripVortexLockEntries(head), stripVortexLockEntries(worktree));
}

/**
 * `lock` minus vortex-scoped package entries and metadata. The root `""` entry
 * is NOT dropped wholesale — it mirrors package.json, so only its `@vortex-os/*`
 * pins are filtered (via `stripVortexPins`) and everything else in it still
 * has to match: dropping the whole entry would let a foreign root change
 * (`packages[""].dependencies.lodash`) ride a "vortex-scoped" verdict. Kept
 * maps are null-prototype for the same `__proto__` reason as stripVortexPins.
 */
function stripVortexLockEntries(lock: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...lock };
  delete out["lockfileVersion"];
  const packages = out["packages"];
  if (isPlainObject(packages)) {
    const kept: Record<string, unknown> = Object.create(null);
    for (const [path, entry] of Object.entries(packages)) {
      if (path === "") {
        kept[path] = isPlainObject(entry) ? stripVortexPins(entry) : entry;
        continue;
      }
      if (path.startsWith("node_modules/" + VORTEX_SCOPE)) continue;
      if (path.includes("/node_modules/" + VORTEX_SCOPE)) continue;
      kept[path] = entry;
    }
    out["packages"] = kept;
  }
  const legacy = out["dependencies"];
  if (isPlainObject(legacy)) {
    const kept: Record<string, unknown> = Object.create(null);
    for (const [name, entry] of Object.entries(legacy)) {
      if (!name.startsWith(VORTEX_SCOPE)) kept[name] = entry;
    }
    out["dependencies"] = kept;
  }
  return out;
}

/**
 * Fold CRLF and lone CR to LF. The update hash-guard compares template CONTENT,
 * not its line-ending encoding: a Windows (CRLF) checkout of a shipped LF
 * template is the SAME file and must hash identically, or every router/command
 * file would be misreported as `locally-modified`. Text-only — every tracked
 * template is UTF-8 text (`routers/*`, `commands/*.md`, `config/*.json`); there
 * are no binary templates, so decoding to a string is safe.
 */
function normalizeEol(input: Buffer | string): string {
  const s = typeof input === "string" ? input : input.toString("utf8");
  return s.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
}

function sha256(buf: Buffer | string): string {
  return createHash("sha256").update(normalizeEol(buf)).digest("hex");
}

async function sha256File(absPath: string): Promise<string> {
  return sha256(await readFile(absPath));
}

/**
 * Does `bytes` reproduce a pre-v2 RAW-byte hash once you account for EOL? A v1
 * ownership manifest stored `createHash(rawBytes)` — on Windows that captured
 * CRLF, on Linux LF. To recognize "same content as when this hash was stored"
 * without the original bytes, hash the current content in BOTH canonical EOL
 * forms (raw, un-normalized) and see if either matches. Migration-only.
 */
function matchesLegacyRawHash(legacyRawHash: string, bytes: Buffer): boolean {
  const raw = (s: Buffer | string): string => createHash("sha256").update(s).digest("hex");
  // Exact raw-byte match first: the strongest "same content as recorded" signal,
  // and the ONLY one that recognizes a v1 hash taken over mixed-EOL or lone-CR
  // bytes (where neither the all-LF nor all-CRLF reconstruction reproduces it).
  if (raw(bytes) === legacyRawHash) return true;
  const lf = normalizeEol(bytes);
  const crlf = lf.replace(/\n/g, "\r\n");
  return raw(lf) === legacyRawHash || raw(crlf) === legacyRawHash;
}

/**
 * Map a template-relative path (as listed in the template index, e.g.
 * `routers/AGENTS.md`) to the instance destination, repoRoot-relative. The
 * SINGLE place that knows the init copy layout — `init` (manifest writer) and
 * `update` both derive destinations here, so they can never drift apart.
 *
 * Returns `null` for paths that are not copied into an instance (the index file
 * itself, or any unrecognized top-level dir) — those are not framework-owned
 * instance files and are skipped.
 */
export function templateDestRelPath(templateRelPath: string): string | null {
  const parts = templateRelPath.split("/");
  if (parts.length < 2) return null; // top-level file (e.g. manifest.json)
  // Reject any `..` / `.` / empty segment, and any segment carrying a
  // BACKSLASH: a legitimate template path is always plain `<family>/<name>`
  // with forward slashes, so traversal can only come from a malformed/hostile
  // index — including the Windows-only shape `guides/roles\..\..\x`, where the
  // backslashes survive this `/`-split as one segment and `join()` would later
  // expand them into real separators (which `assertUnderRoot` would NOT catch
  // while the result stays inside the root). Defense-in-depth above the
  // containment guard.
  if (parts.some((p) => p === ".." || p === "." || p === "" || p.includes("\\"))) return null;
  const [top, ...rest] = parts;
  const tail = rest.join("/");
  if (top === "routers") return tail; // → instance root (AGENTS.md, .cursorrules, …)
  if (top === "commands") return join(".claude", "commands", tail);
  if (top === "config") return join(".agent", tail);
  if (top === "guides") return join(".agent", "guides", tail); // cross-agent on-demand guides (first-steps coach etc.)
  return null; // unknown layout — not managed
}

/**
 * Containment guard: a computed destination must stay under the instance root.
 * The destinations derive from the shipped (trusted) template index, but this
 * is cheap defense-in-depth against a malformed index path ever escaping.
 */
function assertUnderRoot(rootAbs: string, candidateAbs: string): void {
  const rel = relative(rootAbs, candidateAbs);
  const up = ".." + sep;
  const winsensitive = sep === "\\";
  const cmp = winsensitive ? rel.toLowerCase() : rel;
  const upCmp = winsensitive ? up.toLowerCase() : up;
  if (rel === ".." || cmp.startsWith(upCmp) || isAbsolute(rel)) {
    throw new Error(`Refusing to write outside the instance root: ${candidateAbs}`);
  }
}

async function readTemplateIndex(templatesDir: string): Promise<TemplateIndex | null> {
  const indexPath = join(templatesDir, MANIFEST_NAME);
  if (!existsSync(indexPath)) return null;
  try {
    const parsed = JSON.parse(await readFile(indexPath, "utf8")) as TemplateIndex;
    if (!parsed || !Array.isArray(parsed.files)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Reconcile the shipped template index against what is currently on disk and
 * produce an ownership manifest. For each framework-owned file:
 *   - `sourceSha256` is the hash of the actual shipped template file (computed
 *     fresh, not trusted from the index — robust to a stale index);
 *   - `installedSha256` is `sourceSha256` when the on-disk copy matches the
 *     template (pristine — whether `init` wrote it or the user already had the
 *     identical stock content; either way there is nothing to lose), and `null`
 *     when the slot is missing or holds a divergent foreign file.
 *
 * Used by `init` to write the manifest from day one. Pure read — never writes.
 */
export async function buildOwnershipManifest(
  ctx: ModuleContext,
  templatesDir: string,
): Promise<OwnershipManifest | null> {
  const index = await readTemplateIndex(templatesDir);
  if (!index) return null;

  const seenDest = new Set<string>();
  const files: OwnershipEntry[] = [];
  for (const entry of index.files) {
    const destRel = templateDestRelPath(entry.path);
    if (!destRel) continue;
    if (seenDest.has(destRel)) continue; // malformed index: two entries → one dest
    seenDest.add(destRel);
    const shippedAbs = join(templatesDir, entry.path);
    if (!existsSync(shippedAbs)) continue; // index lists a file that isn't there
    const sourceSha256 = await sha256File(shippedAbs);

    const destAbs = join(ctx.repoRoot, destRel);
    assertUnderRoot(ctx.repoRoot, destAbs);
    let installedSha256: string | null = null;
    if (existsSync(destAbs)) {
      const onDisk = await sha256File(destAbs);
      installedSha256 = onDisk === sourceSha256 ? sourceSha256 : null;
    }
    files.push({ templateId: entry.templateId, path: toPosix(destRel), sourceSha256, installedSha256 });
  }
  files.sort((a, b) => a.path.localeCompare(b.path));

  return {
    schema: OWNERSHIP_SCHEMA,
    baseVersion: index.baseVersion,
    generatedAt: new Date().toISOString(),
    files,
  };
}

/**
 * Build and atomically write the ownership manifest for this instance. Called
 * by `vortex init` (best-effort — a failure never blocks init). Returns the
 * written path and file count, or `null` when there is no template index to
 * reconcile against (a dev tree without a built `templates/manifest.json`).
 */
export async function writeOwnershipManifest(
  ctx: ModuleContext,
  templatesDir: string | null,
): Promise<{ path: string; fileCount: number } | null> {
  if (!templatesDir) return null;
  const manifest = await buildOwnershipManifest(ctx, templatesDir);
  if (!manifest) return null;
  const mp = ownershipManifestPath(ctx);
  await mkdir(join(ctx.dataDir, ".vortex"), { recursive: true });
  await atomicWriteFile(mp, JSON.stringify(manifest, null, 2) + "\n");
  return { path: mp, fileCount: manifest.files.length };
}

/** Read-only health snapshot of the ownership manifest vs what's on disk. */
export interface OwnershipDiagnosis {
  /** Whether an ownership manifest exists and parsed. */
  readonly present: boolean;
  /** The manifest file exists on disk but could not be parsed/validated. */
  readonly malformed: boolean;
  readonly total: number;
  /** Tracked file on disk still equals the recorded copy (safe to refresh). */
  readonly pristine: number;
  /** Tracked file on disk diverges from the recorded copy (user edited it). */
  readonly modified: number;
  /** Tracked file is gone from disk (an update would restore it). */
  readonly missing: number;
  /** Slot recorded as foreign (installedSha256 === null) — never auto-managed. */
  readonly unmanaged: number;
}

/**
 * Inspect the ownership manifest against the current instance files — a pure
 * read used by `vortex doctor` to report whether the instance's framework
 * files are stock, user-edited, missing, or foreign. No template index needed
 * (it compares disk to the recorded `installedSha256`, not to a new template).
 */
export async function inspectOwnership(
  ctx: ModuleContext,
  templatesDir?: string | null,
): Promise<OwnershipDiagnosis> {
  let own = await readOwnershipManifest(ctx);
  if (!own) {
    // Distinguish a missing manifest from one that exists but won't parse, so
    // doctor can tell the user to restore/delete a corrupt file rather than
    // "there is no manifest".
    const malformed = existsSync(ownershipManifestPath(ctx));
    return { present: false, malformed, total: 0, pristine: 0, modified: 0, missing: 0, unmanaged: 0 };
  }
  // Migrate a pre-v2 (raw-byte hash) manifest in memory so EOL-only differences
  // are not reported as edits. Read-only by design: doctor never persists here
  // (that is `vortex doctor --repair-manifest`); the rewrite lands on the next
  // `vortex update`. Without a templates dir we cannot migrate — report as-is.
  if (templatesDir && own.schema === OWNERSHIP_SCHEMA_V1) {
    own = await migrateOwnershipToV2(own, ctx, templatesDir);
  }
  let pristine = 0;
  let modified = 0;
  let missing = 0;
  let unmanaged = 0;
  for (const e of own.files) {
    if (e.installedSha256 === null) { unmanaged++; continue; }
    const abs = join(ctx.repoRoot, e.path);
    if (!existsSync(abs)) { missing++; continue; }
    try {
      if ((await sha256File(abs)) === e.installedSha256) pristine++;
      else modified++;
    } catch {
      // Unreadable (locked/permissions) — can't confirm it's stock, so count
      // it conservatively as edited (an update will never auto-replace it).
      modified++;
    }
  }
  return { present: true, malformed: false, total: own.files.length, pristine, modified, missing, unmanaged };
}

/**
 * Adopt / repair the ownership manifest for an instance that lacks one (e.g.
 * created before the update lifecycle existed). Conservative by design: it does
 * NOTHING when a manifest already exists — rewriting could downgrade a tracked-
 * but-edited file to "foreign" and lose its update path — so a healthy instance
 * is never disturbed. Only a missing/unreadable manifest is (re)built from the
 * current on-disk state (diverged files recorded as foreign/null, never falsely
 * claimed pristine). `vortex update --templates-only` is the way to refresh once
 * a manifest exists.
 */
export async function repairOwnershipManifest(
  ctx: ModuleContext,
  templatesDir: string | null,
): Promise<{ status: "created" | "already-present" | "unreadable" | "no-templates"; path?: string; fileCount?: number }> {
  const mp = ownershipManifestPath(ctx);
  // Only ever CREATE when the manifest is genuinely absent. A file that exists
  // but won't parse is NOT silently rebuilt — rebuilding would reclassify a
  // tracked-but-edited file as foreign and lose its update path. Surface it
  // instead so the user restores from a backup or removes it deliberately.
  if (existsSync(mp)) {
    const existing = await readOwnershipManifest(ctx);
    return existing
      ? { status: "already-present", path: mp, fileCount: existing.files.length }
      : { status: "unreadable", path: mp };
  }
  const w = await writeOwnershipManifest(ctx, templatesDir); // null when no template index ships
  return w ? { status: "created", path: w.path, fileCount: w.fileCount } : { status: "no-templates" };
}

async function readOwnershipManifest(ctx: ModuleContext): Promise<OwnershipManifest | null> {
  const mp = ownershipManifestPath(ctx);
  if (!existsSync(mp)) return null;
  try {
    const parsed = JSON.parse(await readFile(mp, "utf8")) as OwnershipManifest;
    if (!parsed || !Array.isArray(parsed.files)) return null;
    // Only known schemas are understood: v2 (current, EOL-normalized hashes) and
    // v1 (legacy raw-byte hashes, migrated in memory on read). An unknown/newer
    // schema (e.g. written by a future version) must NOT be assumed to be v2 —
    // treat it as unreadable so we degrade safely instead of rewriting it wrong.
    if (parsed.schema !== OWNERSHIP_SCHEMA && parsed.schema !== OWNERSHIP_SCHEMA_V1) return null;
    // Validate each entry's shape — a malformed entry (e.g. a null path) would
    // otherwise crash a later `join(repoRoot, e.path)`. Treat any malformation
    // as "unreadable" (return null) rather than as a healthy manifest.
    for (const e of parsed.files) {
      if (
        !e ||
        typeof e.templateId !== "string" ||
        typeof e.path !== "string" ||
        typeof e.sourceSha256 !== "string" ||
        (e.installedSha256 !== null && typeof e.installedSha256 !== "string")
      ) {
        return null;
      }
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Migrate a pre-v2 ownership manifest (raw-byte hashes) to v2 (EOL-normalized
 * hashes) IN MEMORY. v1 hashed raw bytes, so a Windows (CRLF) checkout stored a
 * CRLF hash; once we hash CONTENT (LF-normalized), those stored hashes no longer
 * match and every tracked file would look edited. We recover the true state from
 * the bytes themselves — without needing the (unavailable) old template bytes:
 *   - `srcUnchanged`: the CURRENT template reproduces the stored `sourceSha256`
 *     in some EOL form → upstream content did not change since the manifest.
 *   - `diskPristine`: the on-disk file reproduces the stored `installedSha256`
 *     in some EOL form → the user has not edited it since install.
 * The two v2 hashes are then chosen so the UNCHANGED plan logic — pristine ⟺
 * `curHash === installedSha256`, templateChanged ⟺ `newSource !== sourceSha256`
 * — classifies each file correctly (unchanged / replace / locally-modified /
 * conflict), even when this same release changed the template content.
 * `baseVersion` is preserved so the update report's fromVersion is intact; a
 * successful update advances it as usual. Pure — the caller persists.
 */
async function migrateOwnershipToV2(
  own: OwnershipManifest,
  ctx: ModuleContext,
  templatesDir: string,
): Promise<OwnershipManifest> {
  const index = await readTemplateIndex(templatesDir);
  const tmplAbsById = new Map<string, string>();
  if (index) {
    for (const idx of index.files) {
      if (templateDestRelPath(idx.path)) tmplAbsById.set(idx.templateId, join(templatesDir, idx.path));
    }
  }
  const files: OwnershipEntry[] = [];
  for (const e of own.files) {
    const tmplAbs = tmplAbsById.get(e.templateId);
    if (!tmplAbs || !existsSync(tmplAbs)) {
      files.push(e); // template no longer ships → handled as removed-upstream; hashes unused
      continue;
    }
    const tmplBuf = await readFile(tmplAbs);
    const normTemplate = sha256(tmplBuf);
    if (e.installedSha256 === null) {
      files.push({ ...e, sourceSha256: normTemplate }); // foreign slot stays foreign
      continue;
    }
    const srcUnchanged = matchesLegacyRawHash(e.sourceSha256, tmplBuf);
    const destAbs = join(ctx.repoRoot, e.path);
    let diskPristine = false;
    let normDisk: string | null = null;
    if (existsSync(destAbs)) {
      try {
        const diskBuf = await readFile(destAbs);
        normDisk = sha256(diskBuf);
        diskPristine = matchesLegacyRawHash(e.installedSha256, diskBuf);
      } catch {
        diskPristine = false; // unreadable → treat as edited (never auto-replaced)
      }
    }
    // Choose hashes so the existing plan classifies correctly:
    //   pristine + !templateChanged → unchanged ; pristine + templateChanged → replace
    //   !pristine + !templateChanged → locally-modified ; !pristine + templateChanged → conflict
    const installedSha256 = diskPristine && normDisk ? normDisk : normTemplate;
    // Pure v2: never carry a legacy raw hash forward. `normDisk` is null only when
    // the disk file is missing/unreadable — PLAN handles those via `restore` (or
    // aborts before comparing), so this fallback's value never drives a decision;
    // `normTemplate` keeps the persisted manifest in the normalized hash space.
    const sourceSha256 = srcUnchanged ? normTemplate : (normDisk ?? normTemplate);
    files.push({ templateId: e.templateId, path: e.path, sourceSha256, installedSha256 });
  }
  files.sort((a, b) => a.path.localeCompare(b.path));
  return { schema: OWNERSHIP_SCHEMA, baseVersion: own.baseVersion, generatedAt: own.generatedAt, files };
}

/** A planned per-file operation (computed before any write so dry-run is free). */
interface PlannedOp {
  readonly action: UpdateFileAction;
  /** Shipped template to copy from (for replace/restore/install/conflict). */
  readonly shippedAbs?: string;
  readonly destAbs?: string;
  /** Manifest entry to record after this op (null ⇒ drop from manifest). */
  readonly entry: OwnershipEntry | null;
}

/**
 * `vortex update --templates-only` — refresh framework-owned templates from the
 * currently-installed package, hash-guarded so user edits are never lost. Two
 * phases: PLAN (all reads — safe in dry-run) then APPLY (writes, skipped in
 * dry-run), with the manifest written LAST and always reflecting real disk.
 */
export async function runTemplatesUpdate(
  ctx: ModuleContext,
  templatesDir: string | null,
  options: { dryRun?: boolean; adopt?: ReadonlySet<string> } = {},
): Promise<VortexUpdateResult> {
  const dryRun = options.dryRun ?? false;
  // Dest paths (repoRoot-relative, POSIX) the user explicitly asked to re-adopt:
  // force-refresh to the template and re-track, even if foreign/edited. Empty by
  // default — adoption only ever happens on an explicit `--adopt`.
  const adopt = options.adopt ?? new Set<string>();
  const base: Omit<VortexUpdateResult, "status" | "actions" | "summary" | "nextActions"> = {
    subcommand: "update",
    mode: "templates-only",
    dryRun,
  };

  let own = await readOwnershipManifest(ctx);
  if (!own) {
    return {
      ...base,
      status: "no-manifest",
      actions: [],
      summary: emptySummary(),
      nextActions: [
        "This instance has no ownership manifest (data/.vortex/ownership.json).",
        "Run `/vortex init` to write one (non-destructive — it reconciles against your current files).",
      ],
    };
  }

  const index = templatesDir ? await readTemplateIndex(templatesDir) : null;
  if (!index || !templatesDir) {
    return {
      ...base,
      status: "no-templates",
      actions: [],
      summary: emptySummary(),
      nextActions: [
        "No shipped template index found — the installed @vortex-os/base may be incomplete.",
        "Reinstall the package (`npm i @vortex-os/base`) and retry.",
      ],
    };
  }

  // Migrate a pre-v2 (raw-byte hash) manifest to EOL-normalized hashing in
  // memory before planning, so a CRLF checkout is not misread as edited. The
  // migrated manifest is persisted by the manifest-LAST write below (only when
  // !dryRun) — `migratedFromLegacy` forces that write even if entries are equal.
  const migratedFromLegacy = own.schema === OWNERSHIP_SCHEMA_V1;
  if (migratedFromLegacy) own = await migrateOwnershipToV2(own, ctx, templatesDir);

  const fromVersion = own.baseVersion;
  const toVersion = index.baseVersion;
  const ownByTemplateId = new Map(own.files.map((e) => [e.templateId, e]));
  const seenTemplateIds = new Set<string>();
  const seenDest = new Set<string>();
  // `--adopt` bookkeeping: which named paths matched a real slot (so the rest are
  // reported as unknown), and which were refused because they are user-owned config.
  const adoptMatched = new Set<string>();
  const adoptRefusedConfig: string[] = [];

  // ── PLAN ──
  const ops: PlannedOp[] = [];
  for (const idx of index.files) {
    const destRel = templateDestRelPath(idx.path);
    if (!destRel) continue;
    if (seenDest.has(destRel)) continue; // malformed index: two entries → one dest
    seenDest.add(destRel);
    seenTemplateIds.add(idx.templateId);
    const shippedAbs = join(templatesDir, idx.path);
    if (!existsSync(shippedAbs)) continue;
    const newSource = await sha256File(shippedAbs);

    const destAbs = join(ctx.repoRoot, destRel);
    assertUnderRoot(ctx.repoRoot, destAbs);
    const path = toPosix(destRel);
    const templateId = idx.templateId;
    const exists = existsSync(destAbs);
    const curHash = exists ? await sha256File(destAbs) : null;
    const prior = ownByTemplateId.get(templateId);

    // Explicit `--adopt <path>`: the user chose to force this file back to the
    // shipped template and re-track it, whatever its current state (foreign,
    // edited, or conflicted). Config is user-owned (adoption would wipe live
    // settings) — refuse and fall through to the file's normal (no-write) branch.
    if (adopt.has(path)) {
      adoptMatched.add(path);
      if (!idx.path.startsWith("config/")) {
        if (exists && curHash === newSource) {
          // Already identical to the template — nothing to overwrite; just track.
          ops.push({
            action: { path, templateId, action: "unchanged", detail: "already matches the template — re-tracked" },
            entry: { templateId, path, sourceSha256: newSource, installedSha256: newSource },
          });
        } else {
          ops.push({
            action: { path, templateId, action: "adopt", detail: "re-adopted to the template — your version is backed up" },
            shippedAbs,
            destAbs,
            entry: { templateId, path, sourceSha256: newSource, installedSha256: newSource },
          });
        }
        continue;
      }
      adoptRefusedConfig.push(path); // user-owned config — never adopt; fall through
    }

    // New framework file in this version (no prior manifest entry).
    if (!prior) {
      if (!exists) {
        ops.push({
          action: { path, templateId, action: "install", detail: "new framework file — written" },
          shippedAbs,
          destAbs,
          entry: { templateId, path, sourceSha256: newSource, installedSha256: newSource },
        });
      } else if (curHash === newSource) {
        ops.push({
          action: { path, templateId, action: "unchanged" },
          entry: { templateId, path, sourceSha256: newSource, installedSha256: newSource },
        });
      } else {
        // A foreign file already sits in a newly-shipped slot — never clobber.
        ops.push({
          action: {
            path,
            templateId,
            action: "conflict",
            detail: "a file already exists in a newly-shipped slot — wrote .new",
            newFilePath: toPosix(relative(ctx.repoRoot, destAbs + ".new")),
          },
          shippedAbs,
          destAbs,
          entry: { templateId, path, sourceSha256: newSource, installedSha256: null },
        });
      }
      continue;
    }

    // Foreign slot — on-disk diverges from the template (installedSha256===null).
    // Never auto-OVERWRITTEN (would clobber a user-owned file). But if the file is
    // now byte-identical to the template, there is nothing to lose by tracking it,
    // so re-adopt it automatically (mirrors buildOwnershipManifest's pristine
    // detection) — this rescues a file the user manually brought back in line,
    // which the old `=== null` short-circuit stranded from updates forever.
    if (prior.installedSha256 === null) {
      if (exists && curHash === newSource) {
        ops.push({
          action: { path, templateId, action: "unchanged", detail: "matches the template — re-tracked" },
          entry: { templateId, path, sourceSha256: newSource, installedSha256: newSource },
        });
        continue;
      }
      ops.push({
        action: { path, templateId, action: "unmanaged", detail: "on-disk file diverges from the template — left untouched" },
        entry: { ...prior, sourceSha256: newSource },
      });
      continue;
    }

    // Tracked file that has gone missing → restore it (nothing to clobber).
    if (!exists) {
      ops.push({
        action: { path, templateId, action: "restore", detail: "framework file was missing — restored" },
        shippedAbs,
        destAbs,
        entry: { templateId, path, sourceSha256: newSource, installedSha256: newSource },
      });
      continue;
    }

    const pristine = curHash === prior.installedSha256;
    const templateChanged = newSource !== prior.sourceSha256;

    if (pristine) {
      if (!templateChanged) {
        ops.push({ action: { path, templateId, action: "unchanged" }, entry: { ...prior, sourceSha256: newSource } });
      } else {
        ops.push({
          action: { path, templateId, action: "replace", detail: `${fromVersion} → ${toVersion}` },
          shippedAbs,
          destAbs,
          entry: { templateId, path, sourceSha256: newSource, installedSha256: newSource },
        });
      }
    } else {
      // User edited the file since install.
      if (!templateChanged) {
        ops.push({
          action: { path, templateId, action: "locally-modified", detail: "you edited this; template unchanged — left as-is" },
          entry: { ...prior, sourceSha256: newSource },
        });
      } else {
        ops.push({
          action: {
            path,
            templateId,
            action: "conflict",
            detail: "you edited this and the template changed — wrote .new; your file is untouched",
            newFilePath: toPosix(relative(ctx.repoRoot, destAbs + ".new")),
          },
          shippedAbs,
          destAbs,
          // installedSha256 stays the prior value: the user's file is unchanged.
          entry: { templateId, path, sourceSha256: newSource, installedSha256: prior.installedSha256 },
        });
      }
    }
  }

  // Orphans: tracked entries whose template is no longer shipped. Keep the file
  // (it may hold user content), drop it from the manifest, report it.
  const orphanOps: PlannedOp[] = [];
  for (const e of own.files) {
    if (seenTemplateIds.has(e.templateId)) continue;
    orphanOps.push({
      action: { path: e.path, templateId: e.templateId, action: "removed-upstream", detail: "no longer shipped — file kept, untracked" },
      entry: null,
    });
  }

  const allOps = [...ops, ...orphanOps];

  // ── APPLY (skipped entirely in dry-run) ──
  // Each op is applied under its own try/catch and the manifest entry recorded
  // reflects the ACTUAL post-state on disk — so even a mid-run failure leaves
  // the manifest agreeing with disk, never a stale claim that turns an already-
  // applied file into a phantom conflict next run.
  const appliedActions: UpdateFileAction[] = [];
  const finalEntries: OwnershipEntry[] = [];
  const backupRoot = join(ctx.dataDir, ".vortex", "backups", new Date().toISOString().replace(/[:.]/g, "-"));
  let applyError = false;

  // Write `<dest>.new` without clobbering a `.new` the user may be merging: if
  // one already exists and differs, back it up first; if identical, skip.
  // Returns the backup path (if a divergent `.new` was preserved).
  const writeDotNew = async (destAbs: string, content: string): Promise<string | undefined> => {
    const newPath = destAbs + ".new";
    if (existsSync(newPath)) {
      if ((await readFile(newPath, "utf8")) === content) return undefined; // already parked
      const backupAbs = join(backupRoot, toPosix(relative(ctx.repoRoot, newPath)));
      await mkdir(dirname(backupAbs), { recursive: true });
      await copyFile(newPath, backupAbs);
      await atomicWriteFile(newPath, content);
      return toPosix(relative(ctx.repoRoot, backupAbs));
    }
    await atomicWriteFile(newPath, content);
    return undefined;
  };

  for (const op of allOps) {
    let action = op.action;
    let entry = op.entry; // the state to record (default: no-write outcome)
    if (!dryRun && op.shippedAbs && op.destAbs) {
      const destAbs = op.destAbs;
      try {
        const content = await readFile(op.shippedAbs, "utf8");
        const newSource = op.entry ? op.entry.sourceSha256 : sha256(content);
        if (action.action === "replace") {
          const prior = ownByTemplateId.get(action.templateId);
          if (!existsSync(destAbs)) {
            // Vanished since planning — nothing to clobber; just write it.
            await mkdir(dirname(destAbs), { recursive: true });
            await atomicWriteFile(destAbs, content);
          } else if ((await sha256File(destAbs)) === (prior?.installedSha256 ?? null)) {
            // Re-checked still pristine → safe to back up and replace.
            const backupAbs = join(backupRoot, action.path);
            await mkdir(dirname(backupAbs), { recursive: true });
            await copyFile(destAbs, backupAbs);
            await atomicWriteFile(destAbs, content);
            action = { ...action, backupPath: toPosix(relative(ctx.repoRoot, backupAbs)) };
          } else {
            // Changed since planning — do NOT overwrite the user's edit; park
            // the new template at `.new` (same outcome as a planned conflict).
            const backupPath = await writeDotNew(destAbs, content);
            action = {
              path: action.path,
              templateId: action.templateId,
              action: "conflict",
              detail: "file changed since planning — wrote .new instead of overwriting",
              newFilePath: toPosix(relative(ctx.repoRoot, destAbs + ".new")),
              ...(backupPath ? { backupPath } : {}),
            };
            entry = { templateId: action.templateId, path: action.path, sourceSha256: newSource, installedSha256: prior?.installedSha256 ?? null };
          }
        } else if (action.action === "restore" || action.action === "install") {
          // Re-check at write time: if the absent target reappeared and now
          // differs, park the template at `.new` rather than clobber it.
          if (existsSync(destAbs) && (await sha256File(destAbs)) !== newSource) {
            const backupPath = await writeDotNew(destAbs, content);
            action = {
              path: action.path,
              templateId: action.templateId,
              action: "conflict",
              detail: "target appeared since planning — wrote .new instead of overwriting",
              newFilePath: toPosix(relative(ctx.repoRoot, destAbs + ".new")),
              ...(backupPath ? { backupPath } : {}),
            };
            entry = { templateId: action.templateId, path: action.path, sourceSha256: newSource, installedSha256: null };
          } else {
            await mkdir(dirname(destAbs), { recursive: true });
            await atomicWriteFile(destAbs, content);
          }
        } else if (action.action === "conflict") {
          await mkdir(dirname(destAbs), { recursive: true });
          const backupPath = await writeDotNew(destAbs, content);
          if (backupPath) action = { ...action, backupPath };
        } else if (action.action === "adopt") {
          // Explicit re-adopt: back up whatever the user has on disk, then
          // overwrite with the template. No divert-to-`.new` — the user asked for
          // the template. (The entry already records installedSha256 = newSource.)
          if (existsSync(destAbs)) {
            const backupAbs = join(backupRoot, action.path);
            await mkdir(dirname(backupAbs), { recursive: true });
            await copyFile(destAbs, backupAbs);
            action = { ...action, backupPath: toPosix(relative(ctx.repoRoot, backupAbs)) };
          }
          await mkdir(dirname(destAbs), { recursive: true });
          await atomicWriteFile(destAbs, content);
        }
      } catch (e) {
        applyError = true;
        action = { ...action, error: (e as Error).message };
        // Record the PRIOR entry (or null when there is none) so the manifest
        // stays truthful AND the failed op is retried next run. The dest was
        // never destructively changed (replace is atomic and aborted; restore/
        // install never wrote; conflict only touched `.new`). Falling back to
        // prior keeps `sourceSha256` un-advanced, so a failed conflict/replace
        // is still detected as a pending change and retried rather than being
        // silently dropped (a fresh install with no prior → null → re-planned).
        entry = ownByTemplateId.get(action.templateId) ?? null;
      }
    }
    appliedActions.push(action);
    if (entry) finalEntries.push(entry);
  }

  // ── Manifest LAST — reflects real disk state; written only when it changed ──
  // Dedup by destination path (keep last) so a malformed index can't produce a
  // manifest with duplicate paths.
  const byPath = new Map<string, OwnershipEntry>();
  for (const e of finalEntries) byPath.set(e.path, e);
  const newEntries = [...byPath.values()].sort((a, b) => a.path.localeCompare(b.path));
  const priorSorted = [...own.files].sort((a, b) => a.path.localeCompare(b.path));
  const entriesChanged = JSON.stringify(newEntries) !== JSON.stringify(priorSorted);
  // On a partial failure keep the recorded baseVersion (still mid-migration);
  // only advance it when the whole reconcile to the shipped version succeeded.
  const newBaseVersion = applyError ? fromVersion : toVersion;
  if (!dryRun && (entriesChanged || newBaseVersion !== fromVersion || migratedFromLegacy)) {
    const manifest: OwnershipManifest = {
      schema: OWNERSHIP_SCHEMA,
      baseVersion: newBaseVersion,
      generatedAt: new Date().toISOString(),
      files: newEntries,
    };
    await mkdir(join(ctx.dataDir, ".vortex"), { recursive: true });
    await atomicWriteFile(ownershipManifestPath(ctx), JSON.stringify(manifest, null, 2) + "\n");
  }

  const summary = summarize(appliedActions);
  const changes = summary.replaced + summary.restored + summary.installed + summary.adopted;
  const status: VortexUpdateResult["status"] = dryRun
    ? "dry-run"
    : summary.conflicts > 0
      ? "conflicts"
      : changes > 0
        ? "updated"
        : "ok";

  return {
    ...base,
    status,
    fromVersion,
    toVersion,
    actions: appliedActions,
    summary,
    nextActions: buildNextActions(status, summary, appliedActions, dryRun, fromVersion, toVersion, {
      adoptRefusedConfig,
      adoptUnknown: [...adopt].filter((p) => !adoptMatched.has(p)),
    }),
  };
}

function emptySummary(): VortexUpdateResult["summary"] {
  return { replaced: 0, restored: 0, installed: 0, conflicts: 0, unchanged: 0, unmanaged: 0, adopted: 0, locallyModified: 0, removedUpstream: 0, errors: 0 };
}

function summarize(actions: readonly UpdateFileAction[]): VortexUpdateResult["summary"] {
  const s = {
    replaced: 0, restored: 0, installed: 0, conflicts: 0,
    unchanged: 0, unmanaged: 0, adopted: 0, locallyModified: 0, removedUpstream: 0, errors: 0,
  };
  for (const a of actions) {
    // An errored apply did NOT change the file destructively — count it as an
    // error, not as the success it was planned to be.
    if (a.error) { s.errors++; continue; }
    if (a.action === "replace") s.replaced++;
    else if (a.action === "restore") s.restored++;
    else if (a.action === "install") s.installed++;
    else if (a.action === "conflict") s.conflicts++;
    else if (a.action === "unchanged") s.unchanged++;
    else if (a.action === "unmanaged") s.unmanaged++;
    else if (a.action === "adopt") s.adopted++;
    else if (a.action === "locally-modified") s.locallyModified++;
    else if (a.action === "removed-upstream") s.removedUpstream++;
  }
  return s;
}

function buildNextActions(
  status: VortexUpdateResult["status"],
  summary: VortexUpdateResult["summary"],
  actions: readonly UpdateFileAction[],
  dryRun: boolean,
  fromVersion: string,
  toVersion: string,
  adopt: { adoptRefusedConfig: readonly string[]; adoptUnknown: readonly string[] } = {
    adoptRefusedConfig: [],
    adoptUnknown: [],
  },
): string[] {
  const out: string[] = [];
  if (dryRun) {
    const changes = summary.replaced + summary.restored + summary.installed + summary.adopted;
    out.push(
      changes + summary.conflicts === 0
        ? `Dry run: templates already current (base ${toVersion}). Nothing would change.`
        : `Dry run (base ${fromVersion} → ${toVersion}): would update ${changes}, conflict ${summary.conflicts}. Re-run without --dry-run to apply.`,
    );
  } else if (status === "ok") {
    out.push(`Templates already current (base ${toVersion}). Nothing to do.`);
  } else if (status === "updated") {
    const n = summary.replaced + summary.restored + summary.installed + summary.adopted;
    out.push(
      summary.errors > 0
        ? `Refreshed ${n} framework file(s); ${summary.errors} could not be applied — base stays ${fromVersion} until those resolve. Backups under data/.vortex/backups/.`
        : `Refreshed ${n} framework file(s) to base ${toVersion}. Backups under data/.vortex/backups/.`,
    );
  } else if (status === "conflicts") {
    out.push(`Updated what was safe; ${summary.conflicts} file(s) you edited were left untouched — the new template is alongside as \`<file>.new\`.`);
    for (const a of actions) {
      if (a.action === "conflict" && a.newFilePath) out.push(`  conflict: ${a.path} — review ${a.newFilePath} and merge by hand.`);
    }
  }
  if (summary.adopted > 0) {
    out.push(
      dryRun
        ? `Would re-adopt ${summary.adopted} file(s) to the template (your versions would be backed up). Re-run without --dry-run to apply.`
        : `Re-adopted ${summary.adopted} file(s) to the template — your prior versions are backed up under data/.vortex/backups/.`,
    );
  }
  if (summary.errors > 0) {
    out.push(`⚠️ ${summary.errors} file(s) could not be applied (left unchanged) — see the per-file \`error\`. Re-run after resolving; your files are intact.`);
  }
  // Divergent foreign files split into two kinds with different remedies: framework
  // files the user can re-adopt to the template, and user-owned config that is
  // intentionally never updated (new options fall back to defaults instead).
  const unmanaged = actions.filter((a) => a.action === "unmanaged");
  const adoptable = unmanaged.filter((a) => !a.path.startsWith(".agent/"));
  const userOwned = unmanaged.filter((a) => a.path.startsWith(".agent/"));
  if (adoptable.length > 0) {
    out.push(
      `${adoptable.length} framework file(s) diverge from the template and are NOT getting updates — run \`vortex update --adopt <path>\` to refresh one to the template (your version is backed up first):`,
    );
    for (const a of adoptable) out.push(`  diverged: ${a.path}`);
  }
  if (userOwned.length > 0) {
    out.push(`${userOwned.length} user-owned file(s) (your settings) are intentionally not updated — new options fall back to defaults.`);
  }
  for (const p of adopt.adoptRefusedConfig) {
    out.push(`--adopt ${p}: refused — that is your settings file; adopting would wipe your settings, and new options already fall back to defaults.`);
  }
  for (const p of adopt.adoptUnknown) {
    out.push(`--adopt ${p}: not a framework-managed file — nothing to adopt.`);
  }
  if (summary.locallyModified > 0) out.push(`${summary.locallyModified} file(s) you edited are unchanged upstream — kept as-is.`);
  return out;
}
